import { SliceCreator } from "@/features/vidyo-call/types/vidyo-common";
import { AuthErrorPayload, PublicSession } from "@classview/shared-types";

import { CallSettingsTabType } from "../types/tab.types";

export interface CallSettingsState {
  /**
   * Session ID (set on the pre-call screen)
   * This is used to identify the session in the backend
   * Different than roomKey
   */
  sessionId: string;
  /**
   * User's name (set on the pre-call screen)
   */
  displayName: string;
  /**
   * Call PIN (set on the pre-call screen)
   */
  pin?: number;
  /**
   * Room key (set on the pre-call screen)
   * You can get this from the URL,
   * but if its not set in the store, we redirect to the pre-call page
   */
  roomKey?: string;
  isVidyoConnected: boolean;
  isRecording: boolean;
  settingsDrawer: {
    isOpen: boolean;
    close: () => void;
    open: () => void;
  };
  participantsDrawer: {
    isOpen: boolean;
    close: () => void;
    open: () => void;
  };
  chatDrawer: {
    isOpen: boolean;
    close: () => void;
    open: () => void;
  };
  moderationDrawer: {
    activeTab: CallSettingsTabType | null;
    conflictingSessions?: PublicSession[];
    isOpen: boolean;
    close: () => void;
    open: (tab?: CallSettingsTabType) => void;
    setConflictingSessions: (sessions?: PublicSession[]) => void;
    setActiveTab: (tab?: CallSettingsTabType) => void;
  };
  setDisplayName: (name: string) => void;
  setPin: (pin: number | undefined) => void;
  setRoomKey: (key?: string) => void;
  setSession: ({
    id,
    parentSession,
  }: {
    id: string;
    parentSession: {
      id: string;
      roomKey: string;
    } | null;
  }) => void;
  setIsVidyoConnected: (isVidyoConnected: boolean) => void;
  /**
   * @description Does not reset sessionId, breakoutSession.parentSessionId
   */
  resetCall: () => void;
  setIsRecording: (isRecording: boolean) => void;
  webSocket: {
    isAuthenticated: boolean;
    authError: AuthErrorPayload | null;
    setAuthError: (error: AuthErrorPayload | null) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
  };
}

export const createCallSettingsSlice: SliceCreator<keyof CallSettingsState> = (
  set,
  _,
  store,
) => {
  store.subscribe((state, prevState) => {
    // reset conflicting sessions when active tab changes
    if (
      state.moderationDrawer.activeTab !== "extend-session" &&
      prevState.moderationDrawer.activeTab === "extend-session"
    ) {
      set(
        (state) => {
          state.moderationDrawer.conflictingSessions = undefined;
        },
        undefined,
        "callSettingsSlice/resetConflictingSessions",
      );
    }

    // reset active tab when drawer closes
    if (
      state.moderationDrawer.isOpen === false &&
      prevState.moderationDrawer.isOpen === true
    ) {
      setTimeout(() => {
        set(
          (state) => {
            state.moderationDrawer.activeTab = null;
          },
          undefined,
          "callSettingsSlice/resetActiveTab",
        );
      }, 400); // Match the drawer's transition duration
    }
  });

  return {
    displayName: "",
    pin: undefined,
    sessionId: "",
    isVidyoConnected: false,
    isRecording: false,
    settingsDrawer: {
      isOpen: false,
      close: () => {
        set(
          (state) => {
            state.settingsDrawer.isOpen = false;
          },
          undefined,
          "settingsDrawer/close",
        );
      },
      open: () => {
        set(
          (state) => {
            state.settingsDrawer.isOpen = true;
            state.participantsDrawer.isOpen = false;
            state.chatDrawer.isOpen = false;
            state.moderationDrawer.isOpen = false;
          },
          undefined,
          "settingsDrawer/open",
        );
      },
    },
    participantsDrawer: {
      isOpen: false,
      close: () => {
        set(
          (state) => {
            state.participantsDrawer.isOpen = false;
          },
          undefined,
          "participantsDrawer/close",
        );
      },
      open: () => {
        set(
          (state) => {
            state.participantsDrawer.isOpen = true;
            // close all other drawers
            state.settingsDrawer.isOpen = false;
            state.chatDrawer.isOpen = false;
            state.moderationDrawer.isOpen = false;
          },
          undefined,
          "participantsDrawer/open",
        );
      },
    },
    setDisplayName: (name: string) => {
      set(
        (state) => {
          state.displayName = name;
        },
        undefined,
        "setDisplayName",
      );
    },
    setPin: (pin: number | undefined) => {
      set(
        (state) => {
          state.pin = pin;
        },
        undefined,
        "setPin",
      );
    },
    setRoomKey: (key?: string) => {
      set(
        (state) => {
          state.roomKey = key;
        },
        undefined,
        "setRoomKey",
      );
    },
    setSession: ({
      id,
      parentSession,
    }: {
      id: string;
      parentSession: {
        id: string;
        roomKey: string;
      } | null;
    }) => {
      set(
        (state) => {
          state.sessionId = id;
          state.breakoutSession.parentSession = parentSession;
        },
        undefined,
        "setSession",
      );

      // reset breakout session if we're joining a breakout session
      if (parentSession !== null) {
        set(
          (state) => {
            state.breakoutSession.shouldJoinToBreakoutSession = false;
            state.breakoutSession.assignedBreakoutRoomKey = null;
          },
          undefined,
          "setSession/resetBreakoutSession",
        );
      } else {
        set(
          (state) => {
            state.breakoutSession.shouldReturnToParentSession = false;
            state.breakoutSession.redirectReason = null;
          },
          undefined,
          "setSession/resetReturnToParentSession",
        );
      }
    },
    chatDrawer: {
      isOpen: false,
      close: () => {
        set(
          (state) => {
            state.chatDrawer.isOpen = false;
          },
          undefined,
          "chatDrawer/close",
        );
      },
      open: () => {
        set(
          (state) => {
            state.chatDrawer.isOpen = true;
            state.chat.hasUnreadMessages = false;
            // close all other drawers
            state.settingsDrawer.isOpen = false;
            state.participantsDrawer.isOpen = false;
            state.moderationDrawer.isOpen = false;
          },
          undefined,
          "chatDrawer/open",
        );
      },
    },
    moderationDrawer: {
      isOpen: false,
      activeTab: null,
      conflictingSessions: undefined,
      setConflictingSessions: (sessions?: PublicSession[]) => {
        set(
          (state) => {
            state.moderationDrawer.conflictingSessions = sessions;
          },
          undefined,
          "moderationDrawer/setConflictingSessions",
        );
      },
      close: () => {
        set(
          (state) => {
            state.moderationDrawer.isOpen = false;
          },
          undefined,
          "moderationDrawer/close",
        );
      },
      open: (tab?: CallSettingsTabType) => {
        set(
          (state) => {
            state.moderationDrawer.isOpen = true;
            state.moderationDrawer.activeTab = tab ?? null;
            // close all other drawers
            state.settingsDrawer.isOpen = false;
            state.participantsDrawer.isOpen = false;
            state.chatDrawer.isOpen = false;
          },
          undefined,
          "moderationDrawer/open",
        );
      },
      setActiveTab: (tab?: CallSettingsTabType) => {
        set((state) => {
          state.moderationDrawer.activeTab = tab ?? null;
        });
      },
    },
    setIsVidyoConnected: (isVidyoConnected: boolean) => {
      set(
        (state) => {
          state.isVidyoConnected = isVidyoConnected;
        },
        undefined,
        "setIsVidyoConnected",
      );
    },
    /**
     * We dont want to reset sessionId, breakoutSession.parentSessionId
     * Those are reset when `useGetSessionByKey` hook is called
     */
    resetCall: () => {
      set(
        (state) => {
          // state.isVidyoConnected = false;
          state.moderatorIds = [];
          state.participants.participants = [];
          state.chat.messages = [];
          state.chat.hasUnreadMessages = false;
          state.settingsDrawer.isOpen = false;
          state.participantsDrawer.isOpen = false;
          state.chatDrawer.isOpen = false;
          state.moderationDrawer.conflictingSessions = [];
          state.closedCaptioning.enabled = false;
          state.closedCaptioning.messages = [];
          state.closedCaptioning.subscriptionToken = null;
        },
        undefined,
        "vidyo/resetCall",
      );
    },
    setIsRecording: (isRecording: boolean) => {
      set(
        (state) => {
          state.isRecording = isRecording;
        },
        undefined,
        "setIsRecording",
      );
    },
    webSocket: {
      isAuthenticated: false,
      authError: null,
      setAuthError: (error: AuthErrorPayload | null) => {
        set(
          (state) => {
            state.webSocket.authError = error;
          },
          undefined,
          "webSocket/setAuthError",
        );
      },
      setIsAuthenticated: (isAuthenticated: boolean) => {
        set(
          (state) => {
            state.webSocket.isAuthenticated = isAuthenticated;
          },
          undefined,
          "webSocket/setIsAuthenticated",
        );
      },
    },
  };
};
